/**
 * Add item to cart based on variant ID
 * @param {Array} items
 */
const addToCart = async (items) => {
  // Input validation
  if (!items || !Array.isArray(items) || items.length === 0) {
    throw new Error('Items must be a non-empty array');
  }

  // Validate each item has required properties
  for (const item of items) {
    if (!item.id || !item.quantity || typeof item.quantity !== 'number') {
      throw new Error('Each item must have id and numeric quantity');
    }
  }

  const formData = {
    items,
    sections: 'cart-drawer,main-cart',
  };
  try {
    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error(
        `Cart add request failed: ${response.status} ${response.statusText}`
      );
    }

    const parsedResponse = await response.json();

    /* TODO: Add learnq tracking */
    // window._learnq.push(['track', 'Added to Cart', this.item]);

    if (parsedResponse.sections) {
      const { 'cart-drawer': cartDrawer, 'main-cart': mainCart } =
        parsedResponse.sections;
      // update cart
      updateCartDrawerContent(cartDrawer);
      updateCartMainContent(mainCart);
    } else {
      // process the error and show it to the user
      document.dispatchEvent(new CustomEvent('cart:open'));
      const event = new CustomEvent('cart:error', {
        detail: parsedResponse.description || parsedResponse.message,
      });
      document.dispatchEvent(event);
    }
  } catch (error) {
    console.error('Error:', error);
  }
};

/**
 * Update multiple items in cart
 * @param {object} items - { item_key: quantity }
 */
const updateCart = async (items) => {
  // Input validation
  if (!items || typeof items !== 'object' || Object.keys(items).length === 0) {
    throw new Error('Items must be a non-empty object');
  }

  // Validate each quantity is a number
  for (const [key, quantity] of Object.entries(items)) {
    if (typeof quantity !== 'number' || quantity < 0) {
      throw new Error(
        `Invalid quantity for item ${key}: must be a non-negative number`
      );
    }
  }

  try {
    const formData = {
      updates: items,
      sections: 'cart-drawer,main-cart',
    };
    const response = await fetch(
      window.Shopify.routes.root + 'cart/update.js',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      }
    );

    if (!response.ok) {
      throw new Error(
        `Cart update request failed: ${response.status} ${response.statusText}`
      );
    }

    const parsedResponse = await response.json();

    if (parsedResponse.sections) {
      const { 'cart-drawer': cartDrawer, 'main-cart': mainCart } =
        parsedResponse.sections;
      // update cart

      updateCartDrawerContent(cartDrawer);
      updateCartMainContent(mainCart);
    } else {
      // process the error and show it to the user
      const event = new CustomEvent('cart:error', {
        detail: parsedResponse.description || parsedResponse.message,
      });
      document.dispatchEvent(event);
    }
  } catch (error) {
    console.error('Error updating cart:', error);
    throw error;
  }
};

/**
 * Get cart content when we need the cart content without adding, updating or deleting items
 * @param {function} callback
 */

const getCart = async () => {
  try {
    const response = await fetch(
      window.Shopify.routes.root +
        '?sections=header,cart-drawer,main-cart,footer',
      {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Get cart request failed: ${response.status} ${response.statusText}`
      );
    }

    const parsedResponse = await response.json();

    if (parsedResponse) {
      const { 'cart-drawer': cartDrawer } = parsedResponse;
      // update cart
      updateCartDrawerContent(cartDrawer);
    } else {
      // process the error and show it to the user
      document.dispatchEvent(new CustomEvent('cart:open'));
      const event = new CustomEvent('cart:error', {
        detail: parsedResponse.description || parsedResponse.message,
      });
      document.dispatchEvent(event);
    }
    document.dispatchEvent(new CustomEvent('cart:open'));
  } catch (error) {
    console.error('Error:', error);
  }
};

/**
 * Change cart item
 * @param {string} key - can either be line item key or variant ID
 * @param {number} quantity
 * @param {function} callback
 */
const changeItem = async ({ key, quantity, properties, callback }) => {
  // Input validation
  if (!key || typeof key !== 'string') {
    throw new Error('Key must be a non-empty string');
  }

  if (typeof quantity !== 'number' || quantity < 0) {
    throw new Error('Quantity must be a non-negative number');
  }

  if (callback && typeof callback !== 'function') {
    throw new Error('Callback must be a function');
  }

  try {
    const formData = {
      id: key,
      quantity,
      properties,
      sections: 'cart-drawer,main-cart',
    };

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error(
        `Change cart item request failed: ${response.status} ${response.statusText}`
      );
    }

    const cart = await response.json();

    if (cart.sections) {
      const { 'cart-drawer': cartDrawer, 'main-cart': mainCart } =
        cart.sections;

      // update cart count
      updateCartDrawerContent(cartDrawer);
      updateCartMainContent(mainCart);
    } else {
      // process the error and show it to the user
      const event = new CustomEvent('cart:error', {
        detail: cart.description || cart.message,
      });
      document.dispatchEvent(event);
    }

    if (callback) {
      callback(cart);
    }

    return cart;
  } catch (error) {
    console.error('Error changing cart item:', error);
    throw error;
  }
};

const updateCartDrawerContent = (cartDrawer) => {
  // Input validation
  if (!cartDrawer || typeof cartDrawer !== 'string') {
    console.error('Invalid cart drawer HTML provided');
    return;
  }

  // Parse the new cart drawer HTML
  const parser = new DOMParser();
  const newCartDrawerHTML = parser.parseFromString(cartDrawer, 'text/html');
  const tempCartDrawerElement = newCartDrawerHTML.querySelector(
    '[data-component="cartDrawer"]'
  );

  if (!tempCartDrawerElement) {
    console.error('Cart drawer element not found in provided HTML');
    return;
  }

  const cartDrawerElement = document.querySelector(
    '[data-component="cartDrawer"]'
  );

  if (!cartDrawerElement) {
    console.error('Cart drawer element not found in DOM');
    return;
  }

  // Update cart count
  const cartDrawerCount = document.querySelectorAll('[data-cart-count]');
  if (cartDrawerCount && cartDrawerCount.length > 0) {
    const rawNewCartCount = tempCartDrawerElement.dataset.cartDrawerCountNumber;

    const newCartCount = Number(rawNewCartCount) || 0;

    cartDrawerCount.forEach((item) => {
      item.textContent = newCartCount;
      if (newCartCount > 0) {
        item.classList.remove('hidden');
      } else {
        item.classList.add('hidden');
      }
    });
  }

  // Update cart content
  const cartDrawerContent = cartDrawerElement.querySelector(
    '[data-cart-drawer-content]'
  );
  const tempCartDrawerContent = tempCartDrawerElement.querySelector(
    '[data-cart-drawer-content]'
  );

  if (cartDrawerContent && tempCartDrawerContent) {
    cartDrawerContent.innerHTML = tempCartDrawerContent.innerHTML;
  }

  // update cart footer
  const cartDrawerFooter = cartDrawerElement.querySelector(
    '[data-cart-drawer-footer]'
  );
  const tempCartDrawerFooter = tempCartDrawerElement.querySelector(
    '[data-cart-drawer-footer]'
  );
  if (cartDrawerFooter && tempCartDrawerFooter) {
    cartDrawerFooter.innerHTML = tempCartDrawerFooter.innerHTML;
  }

  // update shipping progress
  const cartDrawerShippingText = cartDrawerElement.querySelector(
    '[data-shipping-text]'
  );
  if (cartDrawerShippingText) {
    const newShippingText = tempCartDrawerElement.querySelector(
      '[data-shipping-text]'
    );
    if (newShippingText && cartDrawerShippingText.parentNode) {
      cartDrawerShippingText.parentNode.replaceChild(
        newShippingText,
        cartDrawerShippingText
      );
    }
  }

  // Emit custom event to reattach event listeners
  const event = new CustomEvent('cart:update');
  document.dispatchEvent(event);
};

const updateCartMainContent = (mainCart) => {
  // Input validation
  if (!mainCart || typeof mainCart !== 'string') {
    console.error('Invalid main cart HTML provided');
    return;
  }

  // Parse the new cart drawer HTML
  const parser = new DOMParser();
  const newCartMainHTML = parser.parseFromString(mainCart, 'text/html');
  const tempCartMainElement = newCartMainHTML.querySelector(
    '[data-component="cartMain"]'
  );

  if (!tempCartMainElement) {
    console.error('Cart main element not found in provided HTML');
    return;
  }

  const cartMainElement = document.querySelector('[data-component="cartMain"]');

  if (!cartMainElement) return;

  // Check if the new cart has items or is empty
  const tempCartMainContent = tempCartMainElement.querySelector(
    '[data-cart-main-content]'
  );

  if (!tempCartMainContent) {
    // Cart is empty - replace entire cartMain content
    cartMainElement.innerHTML = tempCartMainElement.innerHTML;
  } else {
    // Cart has items - update specific sections for performance
    const cartMainContent = cartMainElement.querySelector(
      '[data-cart-main-content]'
    );

    if (cartMainContent) {
      cartMainContent.innerHTML = tempCartMainContent.innerHTML;
    }

    // update shipping text
    const shippingText = cartMainElement.querySelector('[data-shipping-text]');
    if (shippingText) {
      // replace the shipping node with the new one
      const newShippingText = tempCartMainElement.querySelector(
        '[data-shipping-text]'
      );
      if (newShippingText && shippingText.parentNode) {
        shippingText.parentNode.replaceChild(newShippingText, shippingText);
      }
    }
  }

  // Emit custom event to reattach event listeners
  const event = new CustomEvent('main-cart:update');
  document.dispatchEvent(event);
};

export { addToCart, updateCart, changeItem, getCart };
